import { OrgErrorCode } from '@today/types'

/**
 * Organization API error mapping (TO-389 organization-admin-ui REQ-010). The API
 * puts its stable code in the message prefix (`"ORG_…: text"`) and a generic
 * `CONFLICT`/`VALIDATION_ERROR` in `error.code`.
 */

export type OrgUiErrorCode = OrgErrorCode | 'generic'

const KNOWN = new Set<string>(Object.values(OrgErrorCode))
const PREFIX = /^([A-Z][A-Z0-9_]*):/

interface ApiErrorShape {
	response?: { status?: number; data?: { error?: { code?: string; message?: string } } }
}

const errorBody = (err: unknown) => (err as ApiErrorShape | null)?.response?.data?.error

/** The HTTP status of an API error, or null for a non-HTTP failure. */
export const httpStatusOf = (err: unknown): number | null => (err as ApiErrorShape | null)?.response?.status ?? null

/** The organization code of an API error, or `generic` for anything unknown. */
export const orgErrorCode = (err: unknown): OrgUiErrorCode => {
	const body = errorBody(err)
	const fromMessage = PREFIX.exec(body?.message ?? '')?.[1]
	for (const candidate of [fromMessage, body?.code]) {
		if (candidate && KNOWN.has(candidate)) return candidate as OrgErrorCode
	}
	return 'generic'
}

const FIELD_OF: Partial<Record<OrgUiErrorCode, string>> = {
	[OrgErrorCode.UNIT_CODE_TAKEN]: 'code',
	[OrgErrorCode.TYPE_CODE_TAKEN]: 'code',
	[OrgErrorCode.VALIDITY_INVALID]: 'validTo',
	[OrgErrorCode.ASSIGNMENT_VALIDITY_INVALID]: 'validTo',
	[OrgErrorCode.LOCATION_COORDINATES_INCOMPLETE]: 'latitude',
	[OrgErrorCode.LOCATION_PRIMARY_EXISTS]: 'locationType',
}

/** The form field a code is shown on, or null for the form/dialog top. */
export const fieldForCode = (code: OrgUiErrorCode): string | null => FIELD_OF[code] ?? null

/** The `organization` catalog key of a code's message. */
export const errorMessageKey = (code: OrgUiErrorCode): string => `errors.${code}`

/** A translated refusal: either on a field, or at the top of the form. */
export interface OrgMutationError {
	field: string | null
	message: string
}

/** Map a refusal to its translated message and placement (403/404 without an org code get their own message). */
export const toMutationError = (err: unknown, t: (key: string) => string): OrgMutationError => {
	const code = orgErrorCode(err)
	if (code === 'generic') {
		const status = httpStatusOf(err)
		if (status === 403) return { field: null, message: t('errors.forbidden') }
		if (status === 404) return { field: null, message: t('errors.notFound') }
	}
	return { field: fieldForCode(code), message: t(errorMessageKey(code)) }
}
